import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ArrowLeft, Share2, ChevronDown } from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { Badge } from '@/components/ui/badge';
import UnifiedProgramCard from '@/components/cards/UnifiedProgramCard';
import { User, Camp, UserProgram } from '@/api/entities';
import usePerfectMatches from '@/hooks/usePerfectMatches';
import { useToast } from '@/components/ui/use-toast';
import { useThemeSettings } from '@/contexts/ThemeContext';
import risingSquadLogo from '@/assets/rising-squad.png';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';
import ChildMatchFilter from '@/components/discovery/ChildMatchFilter';
import WhosInModal from '@/components/camp/WhosInModal';
import useDismissedMatches from '@/hooks/useDismissedMatches';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Canonical camp_type values for filter logic (aligned with DB search services)
const OVERNIGHT_CAMP_TYPES = new Set(['Overnight Camp', 'overnight', 'overnight_camp', 'sleepaway', 'residential']);
const DAY_CAMP_TYPES = new Set(['Day Camp', 'day', 'day camp', 'day_camp']);

const PerfectMatchesPage = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [savedPrograms, setSavedPrograms] = useState(new Set());
  const [selectedChildId, setSelectedChildId] = useState(null); // null = "All"
  const [selectedCampType, setSelectedCampType] = useState('all'); // 'all', 'day_camp', 'overnight_camp', 'both'
  const [selectedActivity, setSelectedActivity] = useState('all'); // 'all' or specific activity
  const [isWhosInModalOpen, setIsWhosInModalOpen] = useState(false);
  const [selectedProgramForWhosIn, setSelectedProgramForWhosIn] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';

  // Load user and nearby/top-rated camps similar to Dashboard
  useEffect(() => {
    const load = async () => {
      setIsInitialLoading(true);
      try {
        const userData = await User.me().catch((err) => {
          console.error('Error loading user profile:', err);
          return null;
        });
        console.log('👤 User profile loaded:', {
          hasUser: !!userData,
          userId: userData?.id,
          hasChildren: !!userData?.children,
          childrenCount: userData?.children?.length || 0,
          hasLocation: !!(userData?.locations?.home?.latitude && userData?.locations?.home?.longitude),
          location: userData?.locations?.home
        });
        setUserProfile(userData);
        
        let campsData = [];
        try {
          // Use new forPerfectMatches method that handles:
          // - Day camps with day_camp radius preference
          // - Overnight/both camps with overnight_camp radius preference
          // - Camps without coordinates but matching user's state
          console.log('📍 Loading camps for perfect matches with intelligent filtering');
          campsData = await Camp.forPerfectMatches(userData, 200);
          console.log(`📍 Found ${campsData.length} camps for perfect matching`);
        } catch (err) {
          console.error('Error loading camps for perfect matches:', err);
          // Fallback to list if forPerfectMatches fails
          campsData = await Camp.list("-rating", 50).catch(() => {
            console.error('Failed to load fallback camps');
            return [];
          });
        }
        console.log(`✅ Loaded ${campsData.length} camps for matching`);
        setPrograms(campsData || []);
      } catch (error) {
        console.error('Error in load function:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    load();
  }, []);

  const { matches, isLoading, error } = usePerfectMatches(userProfile, programs, { maxResults: 500, minScore: 40, enrich: false });
  
  // Store unfiltered matches for child filter (allMatches needed for ChildMatchFilter)
  const allMatches = matches;

  // Manage dismissed matches
  const { dismissMatch, restoreMatch, isDismissed, dismissedMatches } = useDismissedMatches();

  // Filter out dismissed matches first
  const visibleMatches = useMemo(() => {
    return matches.filter(match => {
      const programId = (match.camp || match).place_id || (match.camp || match).id || (match.camp || match).name;
      return !isDismissed(programId);
    });
  }, [matches, dismissedMatches, isDismissed]);
  
  // Extract unique activities and camp types from all matches for filter dropdowns
  const { uniqueActivities, uniqueCampTypes } = useMemo(() => {
    const activitiesSet = new Set();
    const campTypesSet = new Set();
    
    visibleMatches.forEach(match => {
      const camp = match.camp || match;
      
      // Extract camp type
      if (camp.camp_type) {
        campTypesSet.add(camp.camp_type);
      }
      
      // Extract activities (normalize comma-separated strings)
      if (camp.activities) {
        const activities = typeof camp.activities === 'string' 
          ? camp.activities.split(',').map(a => a.trim().toLowerCase()).filter(Boolean)
          : Array.isArray(camp.activities)
          ? camp.activities.map(a => String(a).trim().toLowerCase()).filter(Boolean)
          : [];
        
        activities.forEach(activity => {
          if (activity) {
            // Capitalize first letter for display
            const capitalized = activity.charAt(0).toUpperCase() + activity.slice(1);
            activitiesSet.add(capitalized);
          }
        });
      }
    });
    
    return {
      uniqueActivities: Array.from(activitiesSet).sort(),
      uniqueCampTypes: Array.from(campTypesSet).sort()
    };
  }, [visibleMatches]);

  // Filter matches based on selected child, camp type, and activity
  const filteredMatches = useMemo(() => {
    let filtered = visibleMatches;
    
    // Filter by child
    if (selectedChildId) {
      filtered = filtered.filter(match => 
        match.matchedChildren?.some(child => {
          const childId = child.childId || child.id || child.name;
          return String(childId) === String(selectedChildId);
        })
      );
    }
    
    // Filter by camp type (Day/Overnight include "both"; "both" is exact; fallback strict equality)
    if (selectedCampType !== 'all') {
      const isOvernightSelection = OVERNIGHT_CAMP_TYPES.has(selectedCampType) || selectedCampType === 'overnight_camp';
      const isDaySelection = DAY_CAMP_TYPES.has(selectedCampType) || selectedCampType === 'day_camp';
      if (isOvernightSelection) {
        filtered = filtered.filter(match => {
          const ct = (match.camp || match).camp_type;
          return OVERNIGHT_CAMP_TYPES.has(ct) || ct === 'both';
        });
      } else if (isDaySelection) {
        filtered = filtered.filter(match => {
          const ct = (match.camp || match).camp_type;
          return DAY_CAMP_TYPES.has(ct) || ct === 'both';
        });
      } else if (selectedCampType === 'both') {
        filtered = filtered.filter(match => (match.camp || match).camp_type === 'both');
      } else {
        filtered = filtered.filter(match => (match.camp || match).camp_type === selectedCampType);
      }
    }
    
    // Filter by activity
    if (selectedActivity !== 'all') {
      filtered = filtered.filter(match => {
        const camp = match.camp || match;
        if (!camp.activities) return false;
        
        // Normalize activities for comparison
        const activities = typeof camp.activities === 'string'
          ? camp.activities.split(',').map(a => a.trim().toLowerCase()).filter(Boolean)
          : Array.isArray(camp.activities)
          ? camp.activities.map(a => String(a).trim().toLowerCase()).filter(Boolean)
          : [];
        
        // Compare with selected activity (lowercase)
        const selectedActivityLower = selectedActivity.toLowerCase();
        return activities.some(activity => activity === selectedActivityLower);
      });
    }
    
    return filtered;
  }, [visibleMatches, selectedChildId, selectedCampType, selectedActivity]);

  // Get dismissed matches for restoration section
  const dismissedMatchesList = useMemo(() => {
    if (!dismissedMatches || dismissedMatches.length === 0) return [];
    
    return allMatches.filter(match => {
      const programId = (match.camp || match).place_id || (match.camp || match).id || (match.camp || match).name;
      return dismissedMatches.includes(programId);
    });
  }, [allMatches, dismissedMatches]);

  // Load saved programs
  useEffect(() => {
    const loadSavedPrograms = async () => {
      if (!userProfile) return;
      
      try {
        const programs = await UserProgram.list();
        const savedIds = new Set(programs.map(p => 
          p.program_data?.place_id || 
          p.program_data?.id || 
          p.program_id || 
          p.id ||
          `${p.program_data?.name || ''}-${p.program_data?.organization || ''}`
        ));
        setSavedPrograms(savedIds);
      } catch (error) {
        console.error('Error loading saved programs:', error);
      }
    };

    if (userProfile) {
      loadSavedPrograms();
    }
  }, [userProfile]);

  // Handle save/unsave
  const handleSave = useCallback(async (camp) => {
    try {
      if (!userProfile) {
        toast({
          title: 'Please log in',
          description: 'You need to be logged in to save programs.',
          variant: 'destructive',
        });
        return;
      }

      const programIdentifier = camp.place_id || camp.id || camp.name;
      const existingUserPrograms = await UserProgram.filter({
        user_email: userProfile.email,
        "program_data.name": camp.name || camp.title
      });

      const existingProgram = existingUserPrograms.find(p => 
        (p.program_data?.id && p.program_data.id === (camp.place_id || camp.id)) || 
        (!p.program_data?.id && p.program_data?.name === (camp.name || camp.title))
      );

      if (existingProgram) {
        // Remove from saved programs
        await UserProgram.delete(existingProgram.id);
        setSavedPrograms(prev => {
          const newSet = new Set(prev);
          newSet.delete(programIdentifier);
          return newSet;
        });
        toast({
          title: 'Camp removed',
          description: `${camp.name || camp.title} has been removed from your saved camps.`,
        });
      } else {
        // Save program
        const programData = {
          id: camp.place_id || camp.id || null,
          place_id: camp.place_id || null,
          name: camp.name || camp.title,
          address: camp.formatted_address || camp.general_location || camp.location || null,
          latitude: camp.location?.lat || camp.latitude || null,
          longitude: camp.location?.lng || camp.longitude || null,
          general_location: camp.formatted_address || camp.general_location || camp.location || null,
          primary_category: camp.primary_category || camp.category || camp.program_type_inferred || null,
          category: camp.primary_category || camp.category || camp.program_type_inferred || null,
          rating: camp.rating || null,
          user_ratings_total: camp.user_ratings_total || camp.reviewCount || camp.review_count || null,
          photo_url: camp.photos?.[0]?.photoUrl || camp.image_url || null,
          photos: camp.photos || null,
          age_range: camp.ageRange || (camp.age_min && camp.age_max ? `Ages ${camp.age_min}-${camp.age_max}` : null),
          grade_range: camp.grade_range || null,
          cost_structure: camp.price || null,
          distance_miles: camp.distance_miles || camp.distance || null,
          selected_statuses: ['saved']
        };

        const savedProgramData = {
          user_email: userProfile.email,
          program_type: camp.primary_category || camp.category || camp.program_type_inferred || "activity",
          program_data: programData,
          status: 'saved',
          year_tracked: new Date().getFullYear(),
          child_names: [],
          metadata: {
            discovered_at: new Date().toISOString(),
            source: 'perfect_match',
            saved_from: 'perfect_matches_page'
          }
        };

        await UserProgram.create(savedProgramData);
        setSavedPrograms(prev => new Set([...prev, programIdentifier]));
        toast({
          title: 'Camp saved!',
          description: `${camp.name || camp.title} has been added to your saved camps.`,
        });
      }
    } catch (error) {
      console.error('Error saving perfect match camp:', error);
      toast({
        title: 'Error',
        description: 'Failed to save camp. Please try again.',
        variant: 'destructive',
      });
    }
  }, [userProfile, toast]);

  // Handle card click - navigate to program detail page (requires id or place_id; name is not a valid route param)
  const handleCardClick = useCallback((program) => {
    if (!program) return;
    const programId = program.id || program.place_id;
    if (!programId) {
      toast({
        title: 'Cannot open details',
        description: 'This program is missing an identifier. Please try another.',
        variant: 'destructive',
      });
      return;
    }
    const encodedId = encodeURIComponent(String(programId));
    navigate(`/program/${encodedId}?mode=discovery&source=perfect_matches`);
  }, [navigate, toast]);

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Perfect Matches',
        text: `Check out my perfect match recommendations on Summer Squad!`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link copied',
        description: 'Page link copied to clipboard.',
      });
    }
  };

  // Handle Who's In modal
  const handleOpenWhosInModal = useCallback((program) => {
    if (!userProfile) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to see who's in.",
        variant: 'destructive',
      });
      return;
    }
    setSelectedProgramForWhosIn(program);
    setIsWhosInModalOpen(true);
  }, [userProfile, toast]);

  // Handle dismiss match
  const handleDismiss = useCallback((program) => {
    const programId = program.place_id || program.id || program.name;
    dismissMatch(programId);
    toast({
      title: "Match dismissed",
      description: "You can restore it later if needed."
    });
  }, [dismissMatch, toast]);

  return (
    <div className={`min-h-screen ${isDarkTheme ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Top Nav - sticky header like ProgramDetailPage */}
      <div className={`bg-gradient-to-r ${isDarkTheme ? 'from-slate-900 to-slate-800' : 'from-purple-50 to-blue-50'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'} sticky top-0 z-40`}>
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 ${isDarkTheme ? 'hover:bg-slate-800' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
              aria-label="Go back"
            >
              <ArrowLeft className={`w-5 h-5 ${isDarkTheme ? 'text-slate-200' : 'text-gray-700'}`} />
            </button>
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                <img
                  src={risingSquadLogo}
                  alt="Summer Squad logo"
                  className="w-full h-full object-contain"
                  decoding="async"
                  loading="eager"
                  width={40}
                  height={40}
                />
              </div>
              <h1 className={`text-xl font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                Perfect Matches
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      {userProfile?.children && userProfile.children.length > 0 && allMatches.length > 0 && (
        <div className={`${isDarkTheme ? 'bg-slate-800' : 'bg-gray-50'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              {/* Child Filter */}
              <div className="flex-1 min-w-0 w-full md:w-auto">
                <ChildMatchFilter
                  children={userProfile.children}
                  matches={allMatches}
                  selectedChildId={selectedChildId}
                  onChildSelect={setSelectedChildId}
                  className="!bg-transparent !border-0 p-0"
                />
              </div>
              
              {/* Camp Type and Activity Filters */}
              <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto">
                {/* Camp Type Filter */}
                <Select value={selectedCampType} onValueChange={setSelectedCampType}>
                  <SelectTrigger className={`w-full md:w-[140px] ${isDarkTheme ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-gray-200'}`}>
                    <SelectValue placeholder="Camp Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {uniqueCampTypes.map(type => {
                      const displayName = type === 'day_camp' ? 'Day Camp' 
                        : type === 'overnight_camp' ? 'Overnight Camp'
                        : type === 'both' ? 'Both'
                        : type;
                      return (
                        <SelectItem key={type} value={type}>{displayName}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Activity Filter */}
                {uniqueActivities.length > 0 && (
                  <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                    <SelectTrigger className={`w-full md:w-[160px] ${isDarkTheme ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-gray-200'}`}>
                      <SelectValue placeholder="Activity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Activities</SelectItem>
                      {uniqueActivities.map(activity => (
                        <SelectItem key={activity} value={activity}>{activity}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 pt-6">
        {(isLoading || isInitialLoading) && (
          <div className={`rounded-2xl border ${isDarkTheme ? 'border-slate-700 bg-slate-800' : 'border-white/60 bg-white/85'} p-6 text-center shadow-sm backdrop-blur`}>
            <AppLoadingSpinner message="Loading your matches…" size="md" />
          </div>
        )}

        {error && !isLoading && !isInitialLoading && (
          <div className={`rounded-2xl border ${isDarkTheme ? 'border-red-800 bg-red-900/20' : 'border-red-200 bg-white/90'} p-4 ${isDarkTheme ? 'text-red-400' : 'text-red-600'} shadow-sm backdrop-blur`}>
            {error}
          </div>
        )}

        {!isLoading && !isInitialLoading && !error && (
          filteredMatches.length === 0 ? (
            <div className={`rounded-2xl border border-dashed ${isDarkTheme ? 'border-purple-800/70 bg-slate-800' : 'border-purple-200/70 bg-white/85'} p-8 text-center shadow-sm backdrop-blur`}>
              <Icon name="sparkles" size="lg" color="primary" className="mx-auto mb-3 text-brand-500" />
              <h4 className={`mb-2 font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>No Perfect Matches Yet</h4>
              <p className={`text-sm mb-4 ${isDarkTheme ? 'text-slate-400' : 'text-slate-600'}`}>
                {!userProfile 
                  ? 'Please log in to see your perfect matches.'
                  : !userProfile.children || userProfile.children.length === 0
                  ? 'Add children details to your profile to get personalized recommendations.'
                  : (!userProfile.children.some(c => (c.interests && c.interests.length > 0) || (c.preferred_camp_types && c.preferred_camp_types.length > 0)))
                  ? 'Add interests or preferred camp types for your children to get personalized recommendations.'
                  : programs.length === 0
                  ? 'We\'re still loading camps. Please try refreshing the page.'
                  : 'Complete your family profile to get personalized recommendations.'}
              </p>
              {userProfile && (
                <Link 
                  to="/profile#children_details"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkTheme ? 'bg-brand-600 hover:bg-brand-700' : 'bg-brand-600 hover:bg-brand-700'} text-white text-sm font-medium transition-colors`}
                >
                  Go to Profile
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 [&>*:last-child]:border-b-0">
              {filteredMatches.map((match, index) => {
                const program = match.camp || match;
                const programId = program?.id || program?.place_id;
                if (!program || !programId) return null;
                const programIdentifier = program.place_id || program.id;
                return (
                  <UnifiedProgramCard
                    key={`perfect-match-page-${programId}-${index}`}
                    program={program}
                    viewMode="perfectMatch"
                    variant="horizontal"
                    isSaved={savedPrograms.has(programIdentifier)}
                    onSave={() => handleSave(program)}
                    onClick={() => handleCardClick(program)}
                    onWhosIn={handleOpenWhosInModal}
                    onDismiss={handleDismiss}
                    matchData={{ 
                      score: match.score, 
                      confidence: match.confidence, 
                      reason: match.reason,
                      reasons: match.reasons,
                      matchedChildren: match.matchedChildren || []
                    }}
                  />
                );
              })}
            </div>
          )
        )}

        {/* Who's In Modal */}
        {isWhosInModalOpen && selectedProgramForWhosIn && (
          <WhosInModal
            isOpen={isWhosInModalOpen}
            onClose={() => {
              setIsWhosInModalOpen(false);
              setSelectedProgramForWhosIn(null);
            }}
            camp={selectedProgramForWhosIn}
            currentUser={userProfile}
          />
        )}
      </div>

      {/* Dismissed Matches Section */}
      {dismissedMatches.length > 0 && (
        <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} border-t ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button
              onClick={() => setShowDismissed(!showDismissed)}
              className={`flex items-center justify-between w-full p-4 rounded-lg border ${
                isDarkTheme 
                  ? 'border-slate-700 bg-slate-800 hover:bg-slate-750' 
                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
              } transition-colors`}
            >
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                  Dismissed Matches
                </span>
                <Badge variant="secondary" className={isDarkTheme ? 'bg-slate-700 text-slate-200' : ''}>
                  {dismissedMatches.length}
                </Badge>
              </div>
              <ChevronDown 
                className={`w-5 h-5 transition-transform ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'} ${
                  showDismissed ? 'rotate-180' : ''
                }`} 
              />
            </button>
            
            {showDismissed && dismissedMatchesList.length > 0 && (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 [&>*:last-child]:border-b-0">
                {dismissedMatchesList.map((match, index) => {
                  const program = match.camp || match;
                  const programId = program?.place_id || program?.id;
                  if (!program || !programId) return null;
                  const isSaved = savedPrograms.has(programId);
                  return (
                    <UnifiedProgramCard
                      key={`dismissed-match-${programId}-${index}`}
                      program={program}
                      viewMode="perfectMatch"
                      variant="horizontal"
                      isSaved={isSaved}
                      matchData={{
                        score: match.score,
                        confidence: match.confidence,
                        reason: match.reason,
                        reasons: match.reasons,
                        matchedChildren: match.matchedChildren || []
                      }}
                      onSave={() => handleSave(program)}
                      onClick={() => handleCardClick(program)}
                      onWhosIn={handleOpenWhosInModal}
                      onDismiss={(program) => {
                        const programId = program.place_id || program.id || program.name;
                        restoreMatch(programId);
                        toast({
                          title: "Match restored",
                          description: "This match has been added back to your recommendations."
                        });
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerfectMatchesPage;


